import { X, Trash2, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCartStore, cartTotal } from "../store/cart";
import { useGuestCartStore, guestCartTotal } from "../store/guestCart";
import { useAuthStore } from "../store/auth";
import { useCurrencyStore } from "../store/currency";
import { getPriceForSize, formatPrice, resolveAssetUrl } from "../lib/api";

const SIZE_LABEL: Record<string, string> = { A6: "A6 (Card)" };

export default function CartDrawer() {
  const authCart = useCartStore();
  const guestCart = useGuestCartStore();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const { currency, siteMode } = useCurrencyStore();

  const isGuest = !user;
  const items = isGuest ? guestCart.items : authCart.items;
  const open = isGuest ? guestCart.open : authCart.open;
  const toggleOpen = isGuest ? guestCart.toggleOpen : authCart.toggleOpen;

  function handleUpdate(id: string, quantity: number) {
    if (isGuest) guestCart.update(id, quantity);
    else authCart.update(id, quantity);
  }
  function handleRemove(id: string) {
    if (isGuest) guestCart.remove(id);
    else authCart.remove(id);
  }

  const total = isGuest
    ? guestCartTotal(guestCart.items, currency)
    : cartTotal(authCart.items, currency);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={toggleOpen} />

      <aside className="fixed right-0 top-0 z-50 h-full w-full max-w-sm bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-display text-lg font-semibold">{t("cart.title")}</h2>
          <button onClick={toggleOpen} className="p-1.5 text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag size={48} className="text-gray-200" />
            <p className="text-gray-500">{t("cart.empty")}</p>
            <Link to="/shop" onClick={toggleOpen} className="btn-primary">
              {t("cart.browse")}
            </Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4 p-4">
                  <img
                    src={resolveAssetUrl(item.product?.imageUrl)}
                    alt={item.product?.name}
                    className="h-20 w-20 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex flex-1 flex-col gap-1">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">
                      {item.product?.name}
                    </p>
                    {item.size && (
                      <p className="text-xs text-gray-500">Size: {SIZE_LABEL[item.size] ?? item.size}</p>
                    )}
                    <p className="text-sm text-brand-600 font-semibold">
                      {item.product
                        ? formatPrice(getPriceForSize(item.product, item.size, currency), currency)
                        : ""}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => handleUpdate(item.id, item.quantity - 1)}
                        className="w-6 h-6 rounded border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-gray-50 text-sm font-bold"
                      >
                        −
                      </button>
                      <span className="text-sm w-5 text-center">{item.quantity}</span>
                      <button
                        onClick={() => handleUpdate(item.id, item.quantity + 1)}
                        className="w-6 h-6 rounded border border-gray-300 text-gray-600 flex items-center justify-center hover:bg-gray-50 text-sm font-bold"
                      >
                        +
                      </button>
                      <button
                        onClick={() => handleRemove(item.id)}
                        className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t p-5 space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{t("cart.subtotal")}</span>
                <span className="font-semibold text-gray-900">{formatPrice(total, currency)}</span>
              </div>
              <p className="text-xs text-gray-400">{t("cart.shipping")}</p>
              {siteMode === "read_only" ? (
                <div className="bg-gray-50 text-gray-500 text-xs font-bold py-3 px-4 rounded-xl text-center">
                  Checkout is temporarily disabled.
                </div>
              ) : (
                <Link to="/checkout" onClick={toggleOpen} className="btn-primary w-full">
                  {t("cart.checkout")}
                </Link>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
